import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { AppUser } from "@/context/AuthContext";

interface ProfileTabProps {
  user: AppUser;
}

interface ProfileData {
  name: string;
  phone: string;
  address: string;
  birth_date: string;
  gender: string;
}

export default function ProfileTab({ user }: ProfileTabProps) {
  const [form, setForm] = useState<ProfileData>({
    name: user.name || "",
    phone: user.phone || "",
    address: "",
    birth_date: "",
    gender: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadProfile() {
      const { data, error } = await supabase
        .from("vietnamcv_profiles")
        .select("name, phone, address, birth_date, gender")
        .eq("id", user.id)
        .maybeSingle();

      if (!cancelled && !error && data) {
        setForm({
          name: data.name || user.name || "",
          phone: data.phone || user.phone || "",
          address: data.address || "",
          birth_date: data.birth_date || "",
          gender: data.gender || "",
        });
      }
      if (!cancelled) setLoading(false);
    }

    loadProfile();
    return () => { cancelled = true; };
  }, [user.id]);

  const handleChange = (field: keyof ProfileData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setToast({ type: "error", message: "Vui lòng nhập họ tên" });
      return;
    }
    setSaving(true);
    setToast(null);

    const { error } = await supabase
      .from("vietnamcv_profiles")
      .upsert({
        id: user.id,
        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim() || null,
        birth_date: form.birth_date || null,
        gender: form.gender || null,
        updated_at: new Date().toISOString(),
      });

    if (error) {
      setToast({ type: "error", message: "Lỗi khi lưu: " + error.message });
    } else {
      setToast({ type: "success", message: "Đã cập nhật thông tin thành công!" });
    }
    setSaving(false);
    setTimeout(() => setToast(null), 4000);
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-background-100 rounded-lg"></div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-8 pb-6 border-b border-background-200/70">
        <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
          <span className="text-xl font-bold text-primary-600">
            {user.name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div>
          <h3 className="font-heading font-semibold text-lg text-foreground-900">{user.name}</h3>
          <p className="text-sm text-foreground-500">{user.email}</p>
        </div>
      </div>

      {toast && (
        <div
          className={`mb-6 px-4 py-3 rounded-lg text-sm font-medium ${
            toast.type === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <i className={`${toast.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} text-base`}></i>
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">
            Họ và tên <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleChange("name", e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            placeholder="Nhập họ tên"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">Email</label>
          <input
            type="email"
            value={user.email}
            disabled
            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-100 text-sm text-foreground-400 cursor-not-allowed"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">Số điện thoại</label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            placeholder="VD: 0912 345 678"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">Giới tính</label>
          <div className="flex gap-2">
            {[
              { value: "male", label: "Nam" },
              { value: "female", label: "Nữ" },
              { value: "other", label: "Khác" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleChange("gender", form.gender === opt.value ? "" : opt.value)}
                className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  form.gender === opt.value
                    ? "border-primary-400 bg-primary-50 text-primary-700"
                    : "border-background-200 bg-background-50 text-foreground-600 hover:border-background-300"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ngày sinh</label>
          <input
            type="text"
            value={form.birth_date}
            onChange={(e) => handleChange("birth_date", e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            placeholder="dd/mm/yyyy"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">Địa chỉ</label>
          <input
            type="text"
            value={form.address}
            onChange={(e) => handleChange("address", e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            placeholder="Nhập địa chỉ của bạn"
          />
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-background-200/70">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-background-50 border-t-transparent rounded-full animate-spin"></div>
              <span>Đang lưu...</span>
            </>
          ) : (
            <>
              <i className="ri-save-line text-base"></i>
              <span>Lưu thay đổi</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}